import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { uk } from "date-fns/locale";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  FileText,
  Loader2,
  Lock,
  Sparkles,
  Undo2,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/cn";
import { clientRoutes } from "@/lib/clientRoutes";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import {
  fetchInteractionDetail,
  markNotOccurred,
  rescheduleInteraction,
  type InteractionDetail,
} from "@/api/interactions";
import { markMeetingOccurred } from "@/api/meetingsQueue";
import { ApiClientError } from "@/api/client";

/**
 * D-055 S6 — Meeting Detail Drawer (visual v1.2 / екран 3 CTA-баланс).
 *
 * Статус · учасник · мета · очікуваний результат · SLA · report-стан (read-only)
 * + CTA: Проведено / Перенести (inline) / Скасувати (reason) / Заповнити звіт
 * (→ канонічний C+ wizard, передається наверх). «Картка клієнта →» — лінк
 * усередині drawer (Igor Q2). allowedActions — backend-owned (frontend НЕ
 * інферить); 403 → no_access стан (не generic error).
 */

const STATUS_LABELS: Record<string, { label: string; tone: "muted" | "amber" | "red" | "green" | "violet" }> = {
  planned: { label: "Заплановано", tone: "violet" },
  scheduled_in_google: { label: "Заплановано", tone: "violet" },
  past_due_pending_outcome: { label: "Звіт очікується", tone: "amber" },
  completed: { label: "Проведено · звіт подано", tone: "green" },
  occurred_no_report_needed: { label: "Проведено", tone: "green" },
  exception_no_report_required: { label: "Звіт не вимагається (виняток)", tone: "muted" },
  cancelled: { label: "Скасовано", tone: "muted" },
  rescheduled: { label: "Перенесено", tone: "muted" },
};

const TYPE_LABELS: Record<string, string> = {
  first_meeting: "перша зустріч",
  follow_up: "follow-up",
  payment_negotiation: "оплата",
  brand_presentation: "презентація",
  contract_negotiation: "переговори",
  problem_issue: "проблема",
  routine_visit: "візит",
  other: "інше",
};

type Mode = "view" | "reschedule" | "cancel";

export function MeetingDetailDrawer({
  interactionId,
  onClose,
  onFillReport,
  onChanged,
  onFillOnBehalf,
  onMarkOccurredOnBehalf,
}: {
  interactionId: string | null;
  onClose: () => void;
  /** «Заповнити звіт» → канонічний wizard (відкриває сторінка-хост). */
  onFillReport: (d: InteractionDetail) => void;
  /** Після мутації (проведено/перенесено/скасовано) — invalidate черги. */
  onChanged: () => void;
  /** D-151 S2 — on-behalf дії supervisor-а (host-сторінка відкриває confirm/модалку). */
  onFillOnBehalf?: (d: InteractionDetail) => void;
  onMarkOccurredOnBehalf?: (d: InteractionDetail) => void;
}) {
  const open = interactionId !== null;
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();
  const qc = useQueryClient();

  const [mode, setMode] = useState<Mode>("view");
  const [reschedDate, setReschedDate] = useState("");
  const [reschedTime, setReschedTime] = useState("");
  const [reschedReason, setReschedReason] = useState("");
  const [cancelReason, setCancelReason] = useState<"cancelled" | "noshow" | "other">("cancelled");
  const [cancelComment, setCancelComment] = useState("");
  const [actErr, setActErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMode("view");
      setReschedDate("");
      setReschedTime("");
      setReschedReason("");
      setCancelReason("cancelled");
      setCancelComment("");
      setActErr(null);
    }
  }, [open]);

  const q = useQuery({
    queryKey: ["interactionDetail", interactionId],
    queryFn: ({ signal }) => fetchInteractionDetail(interactionId as string, signal),
    enabled: open,
    staleTime: 15_000,
    retry: (count, e) => !(e instanceof ApiClientError && e.status === 403) && count < 2,
  });

  const afterMutation = () => {
    void qc.invalidateQueries({ queryKey: ["interactionDetail", interactionId] });
    onChanged();
    setMode("view");
    setActErr(null);
  };

  const onActError = (e: unknown) =>
    setActErr(e instanceof ApiClientError ? e.message || "Не вдалося виконати дію" : "Не вдалося виконати дію");

  const occurredMut = useMutation({
    mutationFn: () => markMeetingOccurred(interactionId as string, q.data?.interactionRevision ?? 0),
    onSuccess: afterMutation,
    onError: onActError,
  });
  const reschedMut = useMutation({
    mutationFn: () =>
      rescheduleInteraction(interactionId as string, {
        newDate: reschedDate,
        newTime: reschedTime,
        reason: reschedReason.trim() || undefined,
        interactionRevision: q.data?.interactionRevision ?? 0,
      }),
    onSuccess: afterMutation,
    onError: onActError,
  });
  const cancelMut = useMutation({
    mutationFn: () =>
      markNotOccurred(interactionId as string, {
        reason: cancelReason,
        comment: cancelComment.trim() || undefined,
        interactionRevision: q.data?.interactionRevision ?? 0,
      }),
    onSuccess: afterMutation,
    onError: onActError,
  });

  if (!open) return null;

  const fmt = (v: string | null | undefined, pattern = "dd.MM.yyyy HH:mm") => {
    if (!v) return null;
    try {
      return format(parseISO(v.replace(" ", "T")), pattern, { locale: uk });
    } catch {
      return null;
    }
  };

  const d = q.data;
  const noAccess = q.isError && q.error instanceof ApiClientError && q.error.status === 403;
  const status = d ? STATUS_LABELS[d.meetingStatus] ?? { label: d.meetingStatus, tone: "muted" as const } : null;
  const pending = occurredMut.isPending || reschedMut.isPending || cancelMut.isPending;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[480px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="flex items-center gap-2 text-base font-semibold">
            <CalendarDays className="h-4 w-4 text-primary" /> Зустріч
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {q.isPending ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Завантаження…
            </div>
          ) : noAccess ? (
            /* екран 6: «Немає доступу» — НЕ generic error */
            <div className="rounded-lg border bg-muted/30 p-8 text-center">
              <Lock className="mx-auto mb-2 h-5 w-5 text-muted-foreground" aria-hidden />
              <p className="text-sm font-medium">Немає доступу</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Зустрічі цього клієнта поза вашою зоною відповідальності (ACL).
              </p>
            </div>
          ) : q.isError || !d ? (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              Не вдалося завантажити зустріч. Спробуйте ще раз.
            </p>
          ) : (
            <>
              {/* Заголовок + статус */}
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold">{d.summary}</span>
                  {d.meetingType && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] font-semibold text-muted-foreground">
                      {TYPE_LABELS[d.meetingType] ?? d.meetingType}
                    </span>
                  )}
                  {status && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10.5px] font-bold",
                        status.tone === "green" && "bg-primary/10 text-primary",
                        status.tone === "amber" && "bg-warning text-warning-foreground",
                        status.tone === "red" && "bg-destructive/10 text-destructive",
                        status.tone === "violet" && "bg-violet text-violet-foreground",
                        status.tone === "muted" && "bg-muted text-muted-foreground",
                      )}
                    >
                      {!d.reportSubmitted && d.reportOverdueSince ? "Звіт прострочено" : status.label}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {[
                    fmt(d.occurredAt),
                    d.durationMinutes ? `${d.durationMinutes} хв` : null,
                    d.assignedUserName ? `менеджер: ${d.assignedUserName}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>

              {/* Учасники / клієнт (D-134: лід → badge + поля, БЕЗ «Картка клієнта») */}
              <div className="rounded-md border bg-muted/40 px-3 py-2.5 text-sm">
                <div className="flex items-center gap-2">
                  {d.isProspect ? (
                    <Sparkles className="h-4 w-4 shrink-0 text-primary" aria-hidden />
                  ) : (
                    <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <span className="min-w-0 flex-1 truncate">
                    {d.prospect?.name ?? d.accountName ?? "Клієнт не вказаний"}
                    {!d.isProspect && (d.contactName ? ` · ${d.contactName}` : " · контакт не вказаний")}
                  </span>
                  {d.isProspect && (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-semibold text-primary">
                      Потенційний клієнт
                    </span>
                  )}
                </div>

                {/* лід-поля: контакт = ПОЛЯ (НЕ Contact-сутність) */}
                {d.isProspect && d.prospect && (
                  <dl className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                    {d.prospect.contactPerson && <ProspectField label="Контактна особа" value={d.prospect.contactPerson} />}
                    {d.prospect.phone && <ProspectField label="Телефон" value={d.prospect.phone} />}
                    {d.prospect.email && <ProspectField label="Email" value={d.prospect.email} />}
                    {d.prospect.city && <ProspectField label="Місто" value={d.prospect.city} />}
                    {d.prospect.comment && <ProspectField label="Коментар" value={d.prospect.comment} />}
                  </dl>
                )}

                {/* «Картка клієнта» — лише для реального клієнта (holding); лід holding=null */}
                {d.targetHoldingId && (
                  <Link
                    to={`${clientRoutes.client(d.targetHoldingId)}/activities`}
                    className="mt-1.5 inline-block text-xs font-medium text-primary hover:underline"
                    onClick={onClose}
                  >
                    Картка клієнта →
                  </Link>
                )}
              </div>

              {/* Мета / очікуваний результат */}
              {(d.goal || d.expectedResult) && (
                <div className="space-y-2 text-sm">
                  {d.goal && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">Мета</p>
                      <p className="mt-0.5 whitespace-pre-wrap">{d.goal}</p>
                    </div>
                  )}
                  {d.expectedResult && (
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground">Очікуваний результат</p>
                      <p className="mt-0.5 whitespace-pre-wrap">{d.expectedResult}</p>
                    </div>
                  )}
                </div>
              )}

              {/* SLA / report-стан */}
              {!d.reportSubmitted && d.meetingStatus === "past_due_pending_outcome" && (
                <div
                  className={cn(
                    "rounded-md border px-3 py-2.5 text-xs",
                    d.reportOverdueSince
                      ? "border-destructive/40 bg-destructive/5 text-destructive"
                      : "border-warning-foreground/20 bg-warning text-warning-foreground",
                  )}
                >
                  {d.reportOverdueSince ? (
                    <>Звіт прострочено з {fmt(d.reportOverdueSince, "dd.MM HH:mm")} · керівник сповіщений (D-043).</>
                  ) : (
                    <>Звіт очікується · SLA: до {fmt(d.reportSlaDueAt, "dd.MM HH:mm")} (24 робочі години).</>
                  )}
                </div>
              )}

              {/* Поданий звіт — read-only */}
              {d.report && (
                <div className="space-y-2">
                  {/* D-151 S2 (C8) — незмінна атрибуція on-behalf у шапці звіту */}
                  {d.reportSubmissionMode === "supervisor_on_behalf" && (
                    <div className="rounded-md border border-violet-foreground/20 bg-violet px-3 py-2 text-xs text-violet-foreground">
                      Звіт заповнив(ла) <b>{d.reportSubmittedByName ?? "керівник"}</b> (керівник)
                      від імені <b>{d.reportSubmittedForName ?? "менеджера"}</b>
                      {d.reportSubmittedAt ? ` · ${fmt(d.reportSubmittedAt, "dd.MM HH:mm")}` : ""}
                      {d.reportOnBehalfReason ? (
                        <> · Причина: {d.reportOnBehalfReason.length > 120
                          ? d.reportOnBehalfReason.slice(0, 120) + "…"
                          : d.reportOnBehalfReason}</>
                      ) : null}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-semibold text-muted-foreground">
                      Звіт подано {fmt(d.reportSubmittedAt, "dd.MM HH:mm")}
                    </span>
                    {d.report.risk && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-bold text-destructive">
                        <AlertTriangle className="h-3 w-3" aria-hidden /> risk-результат
                      </span>
                    )}
                    {d.reviewStatus === "returned" && (
                      <span className="rounded-full bg-warning px-2 py-0.5 text-[10.5px] font-bold text-warning-foreground">
                        повернуто на уточнення
                      </span>
                    )}
                    {d.reviewStatus === "accepted" && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10.5px] font-bold text-primary">
                        прийнято керівником
                      </span>
                    )}
                  </div>
                  <div className="whitespace-pre-wrap rounded-md border bg-background px-3 py-2.5 text-sm">
                    {d.report.text || "—"}
                  </div>
                  {d.reviewStatus === "returned" && d.reviewComment && (
                    <div className="rounded-md border border-warning-foreground/20 bg-warning px-3 py-2 text-xs text-warning-foreground">
                      <span className="font-semibold">Коментар керівника: </span>
                      {d.reviewComment}
                    </div>
                  )}
                </div>
              )}

              {/* Inline: Перенести */}
              {mode === "reschedule" && (
                <div className="space-y-2 rounded-md border px-3 py-2.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Перенести зустріч <span className="text-destructive">*</span>
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="date" value={reschedDate} onChange={(e) => setReschedDate(e.target.value)} className="h-9 text-sm" />
                    <Input type="time" value={reschedTime} onChange={(e) => setReschedTime(e.target.value)} className="h-9 text-sm" />
                  </div>
                  <Input
                    placeholder="Причина (опц.)"
                    value={reschedReason}
                    onChange={(e) => setReschedReason(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              )}

              {/* Inline: Скасувати */}
              {mode === "cancel" && (
                <div className="space-y-2 rounded-md border px-3 py-2.5">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Чому зустріч не відбудеться? <span className="text-destructive">*</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {([
                      { v: "cancelled", label: "Клієнт скасував" },
                      { v: "noshow", label: "Ми не змогли" },
                      { v: "other", label: "Інша причина" },
                    ] as const).map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setCancelReason(o.v)}
                        className={cn(
                          "rounded-full border px-3 py-1.5 text-xs font-medium",
                          cancelReason === o.v
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border text-muted-foreground",
                        )}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                  <Input
                    placeholder={cancelReason === "other" ? "Коментар (обовʼязково)" : "Коментар (опц.)"}
                    value={cancelComment}
                    onChange={(e) => setCancelComment(e.target.value)}
                    className="h-9 text-sm"
                  />
                </div>
              )}

              {actErr && (
                <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  {actErr}
                </p>
              )}
            </>
          )}
        </div>

        {/* CTA (visual екран 3: Проведено / Перенести / Скасувати / Заповнити звіт) */}
        {d && !noAccess && (
          <div className="border-t px-5 py-3.5">
            {mode === "view" ? (
              <div className="flex flex-wrap gap-2">
                {d.allowedActions.canMarkOccurred && (
                  <button
                    type="button"
                    onClick={() => occurredMut.mutate()}
                    disabled={pending}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {occurredMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    Проведено
                  </button>
                )}
                {d.allowedActions.canFillReport && (
                  <button
                    type="button"
                    onClick={() => onFillReport(d)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  >
                    <FileText className="h-4 w-4" /> Заповнити звіт
                  </button>
                )}
                {d.allowedActions.canReschedule && (
                  <button
                    type="button"
                    onClick={() => setMode("reschedule")}
                    disabled={pending}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
                  >
                    <CalendarClock className="h-4 w-4" /> Перенести
                  </button>
                )}
                {d.allowedActions.canCancel && (
                  <button
                    type="button"
                    onClick={() => setMode("cancel")}
                    disabled={pending}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-muted disabled:opacity-50"
                  >
                    <Undo2 className="h-4 w-4" /> Скасувати
                  </button>
                )}
                {d.allowedActions.canMarkOccurredOnBehalf && onMarkOccurredOnBehalf && (
                  <button
                    type="button"
                    onClick={() => onMarkOccurredOnBehalf(d)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/5"
                  >
                    <CheckCircle2 className="h-4 w-4" /> Проведено (за менеджера)
                  </button>
                )}
                {(d.allowedActions.canFillReportOnBehalf || d.allowedActions.canFillReportOnBehalfBlockedByFreshDraft)
                  && onFillOnBehalf && (
                  <button
                    type="button"
                    onClick={() => onFillOnBehalf(d)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/5"
                  >
                    <FileText className="h-4 w-4" /> Звіт за менеджера
                  </button>
                )}
                {!d.allowedActions.canMarkOccurred
                  && !d.allowedActions.canFillReport
                  && !d.allowedActions.canReschedule
                  && !d.allowedActions.canCancel
                  && !d.allowedActions.canFillReportOnBehalf
                  && !d.allowedActions.canFillReportOnBehalfBlockedByFreshDraft
                  && !d.allowedActions.canMarkOccurredOnBehalf && (
                  <p className="text-xs text-muted-foreground">
                    Дії недоступні — зустріч у завершальному стані або поза вашою зоною.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => (mode === "reschedule" ? reschedMut.mutate() : cancelMut.mutate())}
                  disabled={
                    pending
                    || (mode === "reschedule" && (reschedDate === "" || reschedTime === ""))
                    || (mode === "cancel" && cancelReason === "other" && cancelComment.trim() === "")
                  }
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {mode === "reschedule" ? "Підтвердити перенос" : "Підтвердити скасування"}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("view")}
                  disabled={pending}
                  className="rounded-md border border-border px-3 py-2.5 text-sm font-semibold hover:bg-muted"
                >
                  Назад
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** D-134 — лід-поле у картці зустрічі (контакт = поля на лід-Account). */
function ProspectField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5">
      <dt className="shrink-0 font-medium">{label}:</dt>
      <dd className="min-w-0 break-words">{value}</dd>
    </div>
  );
}
